import { create } from 'zustand';
import {
  acceptEmpathyNorms,
  getEmpathyNorms,
  type EmpathyNormsStatus,
} from '../services/empathy-norms.service';
import { createFlow, onSessionChange, withSession } from './session-read';

interface EmpathyNormsState {
  isOpen: boolean;
  isSubmitting: boolean;
  error: string | null;
  resolvers: Array<(accepted: boolean) => void>;
  /** The served status + copy; the modal renders nothing it was not given. */
  content: EmpathyNormsStatus | null;
  loadState: 'idle' | 'loading' | 'ready' | 'failed';

  request: () => Promise<boolean>;
  load: () => Promise<void>;
  accept: () => Promise<void>;
  decline: () => void;
  /**
   * Back / Escape: decline, unless the accept POST is in flight (then
   * nothing: the server's answer settles the parked request). Explicit
   * buttons and the session/page rules still call `decline` (backlog c-065).
   * True when it declined.
   */
  dismiss: () => boolean;
}

/**
 * "How empathy calls work" (COHORT-COMMUNICATION.md X2) — the circle agreement's
 * twin (stores/circle-agreement.store.ts), and deliberately the same machine: the
 * API interceptor parks a 1:1 join refused with 403 `empathy_norms_required`, this
 * sheet asks, and the join is replayed on Got it.
 *
 * A FLOW (session-read.ts createFlow), like the agreement: each opening is an
 * invocation owned by the session that opened it, so a Got it (or a copy load)
 * that lands after a sign-out or an account switch settles nothing.
 */
const sheet = createFlow();

export const useEmpathyNormsStore = create<EmpathyNormsState>((set, get) => ({
  isOpen: false,
  isSubmitting: false,
  error: null,
  resolvers: [],
  content: null,
  loadState: 'idle',

  request: () =>
    new Promise<boolean>((resolve) => {
      if (get().isOpen && sheet.owns(sheet.current())) {
        set((state) => ({ resolvers: [...state.resolvers, resolve] }));
        return;
      }
      const stale = get().resolvers;
      sheet.open();
      set({
        isOpen: true,
        isSubmitting: false,
        error: null,
        resolvers: [resolve],
        content: null,
        loadState: 'idle',
      });
      stale.forEach((settle) => settle(false));
      void get().load();
    }),

  load: async () => {
    const owner = sheet.current();
    if (!sheet.owns(owner)) return;
    set({ loadState: 'loading' });
    const outcome = await withSession(() => getEmpathyNorms());
    if (!sheet.owns(owner)) return;
    if (outcome.status !== 'ok') {
      set({ loadState: 'failed' });
      return;
    }
    set({ content: outcome.value, loadState: 'ready' });
  },

  accept: async () => {
    const owner = sheet.current();
    if (!sheet.owns(owner)) {
      get().decline();
      return;
    }
    set({ isSubmitting: true, error: null });
    const outcome = await withSession(() => acceptEmpathyNorms());
    if (!sheet.owns(owner)) return;
    if (outcome.status !== 'ok') {
      // Not recorded → not through. The server would refuse the replay anyway;
      // say so instead of springing the button back with no explanation.
      set({
        isSubmitting: false,
        error: "We couldn't save that. Check your connection and try again.",
      });
      return;
    }
    const { resolvers } = get();
    sheet.close();
    set({ isOpen: false, isSubmitting: false, resolvers: [] });
    resolvers.forEach((settle) => settle(true));
  },

  decline: () => {
    const { resolvers } = get();
    sheet.close();
    set({ isOpen: false, isSubmitting: false, error: null, resolvers: [] });
    resolvers.forEach((settle) => settle(false));
  },

  dismiss: () => {
    // Read at the press, not off a render: an accept whose POST has left
    // would land on the server while the parked request was declined here,
    // never replayed (backlog c-065).
    if (get().isSubmitting) return false;
    get().decline();
    return true;
  },
}));

// A session change declines every parked request and closes the sheet.
onSessionChange(() => useEmpathyNormsStore.getState().decline());
